import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import ModuleGate from "@/components/base/ModuleGate";
import UploadField from "@/components/base/UploadField";
import StatusBadge from "@/components/base/StatusBadge";
import { queueEmail, deliveryReadyBody } from "@/lib/queueEmail";
import { createNotification } from "@/lib/createNotification";
import type { Job } from "@/pages/admin/jobs/components/JobModal";
import { deleteFileFromVps } from "@/lib/vpsStorage";

type JobDelivery = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string;
  delivery_type: string;
  title: string;
  description: string | null;
  file_url: string;
  thumbnail_url: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const DELIVERY_TYPES = [
  { value: "gallery", label: "Online Gallery", icon: "ri-gallery-line" },
  { value: "video", label: "Video", icon: "ri-video-line" },
  { value: "zip", label: "ZIP Download", icon: "ri-file-zip-line" },
  { value: "photos", label: "Photos", icon: "ri-image-2-line" },
  { value: "documents", label: "Documents", icon: "ri-file-text-line" },
  { value: "other", label: "Other", icon: "ri-link-m" },
];

const TYPE_STYLES: Record<string, string> = {
  gallery: "bg-violet-50 text-violet-600",
  video: "bg-sky-50 text-sky-600",
  zip: "bg-amber-50 text-amber-600",
  photos: "bg-emerald-50 text-emerald-600",
  documents: "bg-orange-50 text-orange-600",
  other: "bg-slate-100 text-slate-500",
};

const TYPE_ICONS: Record<string, string> = {
  gallery: "ri-gallery-line",
  video: "ri-video-line",
  zip: "ri-file-zip-line",
  photos: "ri-image-2-line",
  documents: "ri-file-text-line",
  other: "ri-link-m",
};

const EMPTY_FORM = {
  delivery_type: "gallery",
  title: "",
  description: "",
  file_url: "",
  thumbnail_url: "",
  expires_at: "",
};
type FormState = typeof EMPTY_FORM;

interface Props {
  job: Job;
  tenantId: string;
  onJobUpdated?: (updated: Partial<Job>) => void;
  clientEmail?: string;
  clientName?: string;
}

function computeDeliveryStatus(deliveries: JobDelivery[]): string {
  const activeNonExpired = deliveries.filter(
    (d) => d.is_active && (!d.expires_at || new Date(d.expires_at) >= new Date()),
  );
  if (activeNonExpired.length > 0) return "prepared";
  if (deliveries.some((d) => d.is_active)) return "expired";
  if (deliveries.length > 0) return "expired";
  return "not_started";
}

export default function DeliveriesTab({ job, tenantId, onJobUpdated, clientEmail, clientName }: Props) {
  const [deliveries, setDeliveries] = useState<JobDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState<JobDelivery | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("job_deliveries")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });
    setDeliveries((data ?? []) as JobDelivery[]);
    setLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const syncJobDeliveryStatus = useCallback(
    async (currentDeliveries: JobDelivery[], forceDelivered?: boolean) => {
      const computed = computeDeliveryStatus(currentDeliveries);
      let newStatus = computed;

      // "delivered" is manual state — preserve it unless force-undelivering or everything gone
      if (job.delivery_status === "delivered" && newStatus !== "not_started" && forceDelivered !== false) {
        newStatus = "delivered";
      }
      if (forceDelivered === true) newStatus = "delivered";
      if (forceDelivered === false) newStatus = computeDeliveryStatus(currentDeliveries);

      if (newStatus === job.delivery_status) return;

      const updatePayload: Record<string, unknown> = {
        delivery_status: newStatus,
        updated_at: new Date().toISOString(),
      };
      if (newStatus === "delivered" && job.delivery_status !== "delivered") {
        updatePayload.delivered_at = new Date().toISOString();
      }
      if (newStatus !== "delivered" && job.delivery_status === "delivered") {
        updatePayload.delivered_at = null;
      }

      const { data: updatedJob, error } = await supabase
        .from("jobs")
        .update(updatePayload)
        .eq("id", job.id)
        .eq("tenant_id", tenantId)
        .select("delivery_status, delivered_at")
        .maybeSingle();

      if (!error && updatedJob && onJobUpdated) {
        onJobUpdated(updatedJob as Partial<Job>);
      }
    },
    [job.id, job.delivery_status, tenantId, onJobUpdated],
  );

  const openNew = () => {
    setEditingDelivery(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (d: JobDelivery) => {
    setEditingDelivery(d);
    setForm({
      delivery_type: d.delivery_type,
      title: d.title,
      description: d.description ?? "",
      file_url: d.file_url,
      thumbnail_url: d.thumbnail_url ?? "",
      expires_at: d.expires_at ? d.expires_at.slice(0, 16) : "",
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingDelivery(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.file_url.trim()) return;
    setSaving(true);
    const payload = {
      delivery_type: form.delivery_type,
      title: form.title,
      description: form.description || null,
      file_url: form.file_url,
      thumbnail_url: form.thumbnail_url || null,
      expires_at: form.expires_at || null,
    };
    let nextDeliveries = [...deliveries];
    let createdDeliveryId: string | null = null;
    if (editingDelivery) {
      const { data } = await supabase
        .from("job_deliveries")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editingDelivery.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (data) {
        nextDeliveries = nextDeliveries.map((d) => (d.id === editingDelivery.id ? (data as JobDelivery) : d));
      }
    } else {
      const { data } = await supabase
        .from("job_deliveries")
        .insert({
          ...payload,
          tenant_id: tenantId,
          job_id: job.id,
          client_id: job.client_id,
          is_active: true,
        })
        .select()
        .maybeSingle();
      if (data) {
        nextDeliveries = [data as JobDelivery, ...nextDeliveries];
        createdDeliveryId = (data as JobDelivery).id;
      }
    }
    setDeliveries(nextDeliveries);
    await syncJobDeliveryStatus(nextDeliveries);

    // ── Queue delivery-ready email if a new delivery was created ──
    if (createdDeliveryId && clientEmail && job.delivery_status !== "delivered") {
      queueEmail({
        tenantId,
        toEmail: clientEmail,
        subject: `Your files are ready — ${job.title}`,
        body: deliveryReadyBody({
          clientName: clientName || "Client",
          jobTitle: job.title,
          portalUrl: `${window.location.origin}/client/portal`,
          deliveryCount: nextDeliveries.length,
        }),
        templateType: "delivery_ready",
        entityType: "job_delivery",
        entityId: createdDeliveryId,
        priority: 3,
        metadata: {
          job_id: job.id,
          job_title: job.title,
          delivery_title: form.title,
          delivery_type: form.delivery_type,
          client_name: clientName,
        },
      });
    }

    setSaving(false);
    cancelForm();
  };

  const handleToggleActive = async (d: JobDelivery) => {
    setBusyId(d.id);
    const { data } = await supabase
      .from("job_deliveries")
      .update({ is_active: !d.is_active, updated_at: new Date().toISOString() })
      .eq("id", d.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    let nextDeliveries = deliveries;
    if (data) {
      nextDeliveries = deliveries.map((x) => (x.id === d.id ? (data as JobDelivery) : x));
      setDeliveries(nextDeliveries);
    }
    await syncJobDeliveryStatus(nextDeliveries);
    setBusyId(null);
  };

  const handleDelete = async (d: JobDelivery) => {
    const result = await Swal.fire({
      title: "Delete Delivery?",
      text: `"${d.title}" will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;
    setBusyId(d.id);

    // Delete from VPS if file exists
    const { data: fileRecord } = await supabase
      .from("uploaded_files")
      .select("id")
      .eq("file_url", d.file_url)
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (fileRecord?.id) {
      try {
        await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
      } catch {
        // Continue even if VPS delete fails
      }
    }
    // Also delete thumbnail if exists
    if (d.thumbnail_url) {
      const { data: thumbRecord } = await supabase
        .from("uploaded_files")
        .select("id")
        .eq("file_url", d.thumbnail_url)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (thumbRecord?.id) {
        try {
          await deleteFileFromVps({ fileId: thumbRecord.id, tenantId, silent: true });
        } catch {
          // Continue
        }
      }
    }

    const { error } = await supabase
      .from("job_deliveries")
      .delete()
      .eq("id", d.id)
      .eq("tenant_id", tenantId);
    let nextDeliveries = deliveries;
    if (!error) {
      nextDeliveries = deliveries.filter((x) => x.id !== d.id);
      setDeliveries(nextDeliveries);
    }
    await syncJobDeliveryStatus(nextDeliveries);
    setBusyId(null);
  };

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyLink = async (d: JobDelivery) => {
    try {
      await navigator.clipboard.writeText(d.file_url);
      setCopiedId(d.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = d.file_url;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedId(d.id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch {
        Swal.fire({ title: "Could not copy", text: "Your browser does not support clipboard copy.", icon: "error", background: "#fff", color: "#1e293b", timer: 2000, showConfirmButton: false });
      }
      document.body.removeChild(ta);
    }
  };

  const activeCount = deliveries.filter((d) => d.is_active).length;

  const handleMarkDelivered = async () => {
    const isDelivered = job.delivery_status === "delivered";
    const result = await Swal.fire({
      title: isDelivered ? "Unmark as delivered?" : "Mark as delivered?",
      text: isDelivered
        ? "This will reset the delivery status to reflect current deliveries."
        : "This will mark this job as delivered to the client.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isDelivered ? "Unmark" : "Mark Delivered",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: isDelivered ? "#94a3b8" : "#10b981",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    await syncJobDeliveryStatus(deliveries, isDelivered ? false : true);

    // ── Notification: delivery marked as delivered ──
    if (!isDelivered) {
      createNotification({
        tenantId,
        type: 'success',
        category: 'delivery',
        title: 'Job marked as delivered',
        message: `"${job.title}" has been marked as delivered to ${clientName || 'the client'}.`,
        entityType: 'job',
        entityId: job.id,
      });
    }
    if (!isDelivered && clientEmail) {
      const activeDeliveries = deliveries.filter((d) => d.is_active);
      queueEmail({
        tenantId,
        toEmail: clientEmail,
        subject: `Your delivery is complete — ${job.title}`,
        body: deliveryReadyBody({
          clientName: clientName || "Client",
          jobTitle: job.title,
          portalUrl: `${window.location.origin}/client/portal`,
          deliveryCount: activeDeliveries.length,
        }),
        templateType: "delivery_ready",
        entityType: "job",
        entityId: job.id,
        priority: 3,
        metadata: {
          job_id: job.id,
          job_title: job.title,
          delivery_count: activeDeliveries.length,
          client_name: clientName,
        },
      });
    }
  };

  return (
    <ModuleGate moduleId="final_deliveries" moduleName="Final Deliveries">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span>{deliveries.length} deliveries</span>
              <span className="text-emerald-600">{activeCount} active</span>
            </div>
            <StatusBadge status={job.delivery_status ?? "not_started"} size="sm" />
          </div>
          <div className="flex items-center gap-2">
            {deliveries.length > 0 && (
              <button
                onClick={handleMarkDelivered}
                className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap border ${
                  job.delivery_status === "delivered"
                    ? "text-slate-500 border-slate-200 hover:bg-slate-50"
                    : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={job.delivery_status === "delivered" ? "ri-arrow-go-back-line" : "ri-check-double-line"}></i>
                </span>
                {job.delivery_status === "delivered" ? "Unmark Delivered" : "Mark Delivered"}
              </button>
            )}
            {!showForm && (
              <button
                onClick={openNew}
                className="flex items-center gap-1.5 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Add Delivery
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <form
            onSubmit={handleSave}
            className="bg-white rounded-xl border border-slate-200 p-5 space-y-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-[#0f172a] font-medium text-sm">
                {editingDelivery ? "Edit Delivery" : "New Delivery"}
              </p>
              <button
                type="button"
                onClick={cancelForm}
                className="text-slate-400 hover:text-[#0f172a] cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Type</label>
                <select
                  value={form.delivery_type}
                  onChange={(e) => setForm((p) => ({ ...p, delivery_type: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#2563eb]/50 transition-colors cursor-pointer"
                >
                  {DELIVERY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  required
                  placeholder="Wedding Gallery 2024"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#2563eb]/50 transition-colors"
                />
              </div>
            </div>
            <UploadField
              label="File / Link URL *"
              value={form.file_url}
              onChange={(url) => setForm((p) => ({ ...p, file_url: url }))}
              accept="*/*"
              hint="Gallery, ZIP, video, or any file"
              urlPlaceholder="https://drive.google.com/..."
              required
              tenantId={tenantId}
              entityType="job_delivery"
              entityId={job.id}
              folder="deliveries"
              maxSizeMB={2000}
            />
            <div className="grid grid-cols-2 gap-4">
              <UploadField
                label="Thumbnail (optional)"
                value={form.thumbnail_url}
                onChange={(url) => setForm((p) => ({ ...p, thumbnail_url: url }))}
                accept="image/*"
                hint="Preview image"
                urlPlaceholder="https://..."
                showPreview
                tenantId={tenantId}
                entityType="job_delivery"
                entityId={job.id}
                folder="deliveries"
                maxSizeMB={20}
              />
              <div>
                <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Expires At</label>
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#2563eb]/50 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-slate-400 text-xs mb-1.5 block uppercase tracking-wider">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                placeholder="Instructions for the client…"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#2563eb]/50 transition-colors resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelForm}
                className="px-4 py-2 text-sm text-slate-400 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-1.5 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-black text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Saving…</>
                ) : (
                  "Save Delivery"
                )}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : deliveries.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 bg-white rounded-xl border border-slate-100">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-50">
              <i className="ri-send-plane-line text-slate-300 text-2xl"></i>
            </div>
            <p className="text-slate-400 text-sm">No deliveries yet</p>
            <button
              onClick={openNew}
              className="text-xs text-[#C8A96E] hover:underline cursor-pointer"
            >
              Add first delivery →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {deliveries.map((d) => {
              const isExpired = !!d.expires_at && new Date(d.expires_at) < new Date();
              const typeLabel = DELIVERY_TYPES.find((t) => t.value === d.delivery_type)?.label ?? d.delivery_type;
              const isBusy = busyId === d.id;
              return (
                <div
                  key={d.id}
                  className={`bg-white rounded-xl border p-4 transition-all ${
                    !d.is_active
                      ? "border-slate-100 opacity-50"
                      : isExpired
                        ? "border-red-200"
                        : "border-slate-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        TYPE_STYLES[d.delivery_type] ?? "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <i className={`${TYPE_ICONS[d.delivery_type] ?? "ri-link-m"} text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-[#0f172a] text-sm font-medium truncate">{d.title}</p>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                              TYPE_STYLES[d.delivery_type] ?? "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {typeLabel}
                          </span>
                          {!d.is_active && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 whitespace-nowrap">
                              Inactive
                            </span>
                          )}
                          {isExpired && d.is_active && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 whitespace-nowrap">
                              Expired
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isBusy ? (
                            <div className="w-4 h-4 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <a
                                href={d.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-all cursor-pointer"
                              >
                                <i className="ri-external-link-line text-sm"></i>
                              </a>
                              <button
                                onClick={() => handleCopyLink(d)}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                                  copiedId === d.id
                                    ? "text-emerald-500 bg-emerald-50"
                                    : "text-slate-400 hover:text-[#0f172a] hover:bg-slate-100"
                                }`}
                                title="Copy link"
                              >
                                <i className={`text-sm ${copiedId === d.id ? "ri-check-line" : "ri-link"}`}></i>
                              </button>
                              <button
                                onClick={() => openEdit(d)}
                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                              >
                                <i className="ri-pencil-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleToggleActive(d)}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                                  d.is_active
                                    ? "text-slate-400 hover:text-amber-500 hover:bg-amber-50"
                                    : "text-emerald-500/50 hover:text-emerald-600 hover:bg-emerald-50"
                                }`}
                              >
                                <i className={`text-sm ${d.is_active ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
                              </button>
                              <button
                                onClick={() => handleDelete(d)}
                                className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {d.description && (
                        <p className="text-slate-500 text-xs mb-1.5 line-clamp-2">{d.description}</p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-slate-300 text-xs truncate max-w-[260px] font-mono">{d.file_url}</p>
                        {d.expires_at && (
                          <span className={`text-xs whitespace-nowrap ${isExpired ? "text-red-400" : "text-slate-400"}`}>
                            {isExpired ? "Expired" : "Expires"}{" "}
                            {new Date(d.expires_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ModuleGate>
  );
}
